import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * SearchableSelect — a drop-in replacement for a <select> that renders
 * a search box at the top of the popup. Ideal for long lists (customers,
 * products, employees, etc).
 *
 * Props:
 *   value      — currently selected option value
 *   onChange   — (value) => void
 *   options    — [{ value, label, sublabel? }] — pre-sorted is fine, we'll
 *                sort again on `label` ascending if sort !== false.
 *   placeholder
 *   disabled
 *   sort       — default true (case-insensitive by label)
 *   width      — CSS width for the trigger (default '100%')
 *   label      — optional floating label shown above the trigger
 *   required
 *   error
 */
export default function SearchableSelect({
  value, onChange, options = [], placeholder = 'Select…',
  disabled, sort = true, width = '100%', label, required, error,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, width: 0 });

  const sortedOptions = useMemo(() => {
    if (!sort) return options;
    return [...options].sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' }));
  }, [options, sort]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedOptions;
    return sortedOptions.filter(o => {
      const l = String(o.label || '').toLowerCase();
      const s = String(o.sublabel || '').toLowerCase();
      return l.includes(q) || s.includes(q);
    });
  }, [sortedOptions, query]);

  const selected = options.find(o => o.value === value);
  const displayLabel = selected ? selected.label : '';

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Keep the popup glued to the trigger on scroll / resize, and FLIP it
  // upward when there's not enough room below the trigger. We compare the
  // viewport space below vs above and pick the side that fits the popup
  // (capped at maxHeight=340 + ~50px chrome). Pads 12px so the popup never
  // kisses the viewport edge.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const POPUP_MAX_H = 340;
    const PAD = 12;
    function reposition() {
      const r = triggerRef.current.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = vh - r.bottom - PAD;
      const spaceAbove = r.top - PAD;
      // Prefer below; flip up only when below isn't enough AND above gives
      // more breathing room. This avoids needless flipping when both sides
      // have plenty of space.
      const flip = spaceBelow < POPUP_MAX_H && spaceAbove > spaceBelow;
      // Cap maxHeight to whatever space is available on the chosen side.
      const availableHeight = flip
        ? Math.max(120, spaceAbove - 4)
        : Math.max(120, spaceBelow);
      const maxHeight = Math.min(POPUP_MAX_H, availableHeight);
      // KEY: when flipping, anchor by BOTTOM (not top) so the popup hugs the
      // trigger regardless of how short its content is. A 3-item dropdown
      // renders right above the trigger; a tall one pushes UP from the same
      // bottom edge. Using `top: r.top - maxHeight` would leave a large gap
      // for short content.
      if (flip) {
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const bottom = vh - r.top + 4;     // popup's bottom edge sits 4px above trigger
        setPos({ left: r.left, bottom, top: 'auto', width: r.width, flip, maxHeight });
      } else {
        setPos({ left: r.left, top: r.bottom + 4, bottom: 'auto', width: r.width, flip, maxHeight });
      }
    }
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  function pick(o) {
    onChange && onChange(o.value);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width }}>
      {label && (
        <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>
          {label}{required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', padding: '8px 32px 8px 12px',
          borderRadius: 7,
          border: '1px solid ' + (error ? 'rgba(239,68,68,0.55)' : 'rgba(255,255,255,0.10)'),
          background: 'rgba(15,23,42,0.6)', color: '#E5E7EB',
          fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative',
          opacity: disabled ? 0.6 : 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        title={displayLabel || placeholder}
      >
        <span style={{ color: displayLabel ? '#E5E7EB' : '#64748B' }}>{displayLabel || placeholder}</span>
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          color: '#94A3B8', fontSize: 10,
        }}>▾</span>
      </button>
      {error && <div style={{ fontSize: 11, color: '#FCA5A5', marginTop: 4 }}>{error}</div>}

      {open && ReactDOM.createPortal(
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: pos.left,
            // When flipping, anchor by bottom so the popup hugs the trigger
            // (no big gap for short content). Otherwise anchor by top.
            top: pos.flip ? 'auto' : pos.top,
            bottom: pos.flip ? pos.bottom : 'auto',
            width: Math.max(pos.width, 260),
            maxHeight: pos.maxHeight || 340,
            display: 'flex', flexDirection: 'column',
            background: 'rgba(15,23,42,0.98)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8,
            boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
            zIndex: 10000,
          }}
        >
          <div style={{ padding: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <input
              ref={inputRef}
              type="text"
              placeholder={`Search… (${sortedOptions.length})`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
                if (e.key === 'Enter' && filtered[0]) { e.preventDefault(); pick(filtered[0]); }
              }}
              style={{
                width: '100%', padding: '7px 10px', borderRadius: 5,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.04)', color: '#E5E7EB',
                fontSize: 12.5, outline: 'none',
              }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 4 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 12px', color: '#64748B', fontSize: 12 }}>No matches for "{query}"</div>
            ) : filtered.map(o => {
              const isSel = o.value === value;
              return (
                <div
                  key={o.value}
                  onClick={() => pick(o)}
                  style={{
                    padding: '7px 10px', borderRadius: 5, cursor: 'pointer',
                    background: isSel ? 'rgba(59,141,248,0.14)' : 'transparent',
                    color: isSel ? '#60A5FA' : '#E5E7EB',
                    fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >
                  {isSel && <span style={{ fontSize: 10 }}>✓</span>}
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                  {o.sublabel && <span style={{ fontSize: 10.5, color: '#64748B' }}>{o.sublabel}</span>}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
